import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import axiosInstance from '../../../Utils/Interceptor';
import SelectBox from '../../../Components/Select/Select';
import CustomModal from 'Components/Modal/CustomModal';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { getCityCoordinates, getCityFromCoordinates } from 'Utils/Functions';
import { useDispatch, useSelector } from 'react-redux';
import { setUserLocation } from '../../../Redux/features/LocationSlice';
import {
  setOpenLocationModal,
  setStateList
} from '../../../Redux/features/StateSlice';
import ReactGoogleAutocomplete from 'react-google-autocomplete';
import LocationChangedDialog from 'Components/Dialog/LocationChangedDialog';
import { setLabAddress } from '../../../Redux/features/AddressSlice';
import { setLoader } from '../../../Redux/features/LoaderSlice';
import DefaultLocationDialog from 'Components/Dialog/DefaultLocationDialog';
import LocationPermission from 'Components/Dialog/LocationPermission'; // Import the dialog component
import { Icon } from '@iconify/react';

const initialValues = {
  city: '',
  state: { value: 16, label: 'Gujarat' },
  latitude: '',
  longitude: '',
  address_id: null
};

const LocationModal = () => {
  const { cityName, cityId, stateName, stateId, lng, lat } = useSelector(
    (state) => state.location
  );
  const { openLocationModal } = useSelector((state) => state.state);
  const [relationList, setRelation] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [selectedState, setSelectedState] = useState(() => {
    if (stateName && stateId) {
      return { label: stateName, value: stateId };
    }
    // fallback to initialValues if available
    if (initialValues.state) {
      return initialValues.state;
    }
    return null;
  });
  const [selectedCity, setSelectedCity] = useState(null);
  const [mapPosition, setMapPosition] = useState({ lat, lng });
  const [center, setCenter] = useState({ lat, lng });
  const mapRef = useRef(null);
  const [mapSearchCoordinates, setMapSearchCoordinates] = useState({
    lat: 0,
    lng: 0
  });
  const [showLocationMismatchModal, setShowLocationMismatchModal] =
    useState(false);
  const [showDefaultLocationModal, setShowDefaultLocationModal] =
    useState(false);
  const { cartData } = useSelector((state) => state?.parsistStates);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const dispatch = useDispatch();
  const panTo = (lat, lng) => {
    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
    }
  };

  const fetchStates = async () => {
    try {
      const { data } = await axiosInstance('/api/user/states');
      if (data) {
        const states = data.data.map((e) => ({
          label: e.state_name,
          value: e.state_id
        }));
        setRelation(states);
        dispatch(setStateList(states));
        stateName && setSelectedState({ label: stateName, value: stateId });
        cityName && setSelectedCity({ label: cityName, value: cityId });
      }
    } catch (error) {}
  };

  const fetchCities = async (stateId) => {
    if (!stateId) return; // Prevent API call if stateId is undefined or falsy
    dispatch(setLoader(true));
    try {
      const { data } = await axiosInstance(
        `/api/user/cities?state_id=${stateId}&location=true`
      );
      if (data) {
        const cities = data.data.map((e) => ({
          label: e.city_name,
          value: e.city_id
        }));
        setCityList(cities);
      }
    } catch (error) {}
    dispatch(setLoader(false));
  };

  const handleOpenLocationMismatchModal = () => {
    setShowLocationMismatchModal(true);
  };
  const handleCloseLocationMismatchModal = () => {
    setShowLocationMismatchModal(false);
  };

  const handleSetLocation = () => {
    // dispatch(setLabAddress(null));
    const { label: cityName } = selectedCity;
    const { label: stateName, value: stateId } = selectedState;
    dispatch(
      setUserLocation({
        cityId: selectedCity.value,
        cityName,
        isLocation: true,
        stateId,
        stateName,
        ...mapPosition
      })
    );
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: () => {
      handleSetDefaultLocation();
    }
  });

  const handleSetDefaultLocation = () => {
    if (cartData?.length > 0 && cityId !== selectedCity.value) {
      handleOpenLocationMismatchModal();
      return;
    } else {
      handleSetLocation();
      dispatch(setOpenLocationModal(false));
    }
  };

  const handleStateChange = (state) => {
    setSelectedState(state);
    setSelectedCity(null);
    fetchCities(state.value);
  };

  const handleCityChange = async (city) => {
    const cityName = city.label;
    const stateName = selectedState
      ? selectedState.label
      : formik.values.state.label;
    setSelectedCity(city);
    const coordinates = await getCityCoordinates(cityName, stateName);
    if (coordinates) {
      setCenter({ lat: coordinates.lat, lng: coordinates.lng });
      setMapPosition({ lat: coordinates.lat, lng: coordinates.lng });
      panTo(coordinates.lat, coordinates.lng);
    }
  };

  const handleMapClick = async (lat, lng) => {
    const cityInfo = await getCityFromCoordinates(lat, lng);
    if (cityInfo) {
      const isMatch = relationList.find(
        (state) => state.label?.toLowerCase() === cityInfo.state?.toLowerCase()
      );

      if (isMatch) {
        const { data } = await axiosInstance(
          `/api/user/cities?state_id=${isMatch.value}&location=true`
        );
        if (data) {
          const city = data.data.find(
            (c) => c.city_name?.toLowerCase() === cityInfo.city?.toLowerCase()
          );
          if (city) {
            setSelectedState(isMatch);
            setSelectedCity({ label: city.city_name, value: city.city_id });
            setMapPosition({ lat, lng });
            setCenter({ lat: lat, lng: lng });
            setMapPosition({ lat: lat, lng: lng });
            return;
          }
        }
      }
    }
    setShowDefaultLocationModal(true);
  };

  const handleDefaultLocation = () => {
    setShowPermissionModal(false);
    setCenter({ lat: 23.0225, lng: 72.5714 });
    setMapPosition({ lat: 23.0225, lng: 72.5714 });
    setSelectedState({ label: 'Gujarat', value: 16 });
    setSelectedCity({ value: 1052, label: 'Ahmedabad' });
  };

  useEffect(() => {
    if (selectedCity && !openLocationModal) {
      handleSetDefaultLocation();
    }
  }, [selectedCity]);

  const requestLocationPermission = () => {
    if ('geolocation' in navigator) {
      // Request approximate location
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const cityInfo = await getCityFromCoordinates(latitude, longitude);

            const stateListResponse = await axiosInstance('/api/user/states');
            const state = stateListResponse?.data?.data.find(
              (state) =>
                state.state_name?.toLowerCase() ===
                cityInfo.state?.toLowerCase()
            );
            if (state) {
              const cityListResponse = await axiosInstance(
                `/api/user/cities?state_id=${state.state_id}`
              );
              const city = cityListResponse?.data?.data.find(
                (c) =>
                  c.city_name?.toLowerCase() === cityInfo.city?.toLowerCase()
              );

              if (city) {
                setCenter({ lat: latitude, lng: longitude });
                setMapPosition({ lat: latitude, lng: longitude });
                setSelectedState({
                  label: state.state_name,
                  value: state.state_id
                });
                setSelectedCity({ label: city.city_name, value: city.city_id });
              } else {
                setShowDefaultLocationModal(true);
              }
            } else {
              setShowDefaultLocationModal(true);
            }
          } catch (error) {
            console.error('Error fetching location data:', error);
            setShowDefaultLocationModal(true);
          }
        },
        () => {
          setShowPermissionModal(true); // Open permission modal if user denies permission
        },
        {
          enableHighAccuracy: false,
          maximumAge: 10000,
          timeout: 5000 // Request approximate location
        }
      );
    } else {
      setShowPermissionModal(true); // Open permission modal if geolocation is not supported
    }
  };

  useEffect(() => {
    fetchStates();
    // cityId && formik.setFieldValue("city", "city");
  }, []);

  useEffect(() => {
    if (relationList.length > 0 && !cityId) {
      requestLocationPermission();
    }
  }, [relationList]);

  useEffect(() => {
    if (selectedState && selectedState.value) {
      fetchCities(selectedState.value);
    }
  }, [selectedState]);

  useEffect(() => {
    return () => {
      formik.resetForm();
    };
  }, []);

  const handleCloseModal = () => {
    dispatch(setOpenLocationModal(false));
    
  };

  const handleRetryPermission = () => {
    setShowPermissionModal(false);
    requestLocationPermission();
  };

  useEffect(() => {
    if (mapSearchCoordinates.lat !== 0 && mapSearchCoordinates.lng !== 0) {
      handleMapClick(mapSearchCoordinates.lat, mapSearchCoordinates.lng);
    }
  }, [mapSearchCoordinates]);

  return (
    <CustomModal
      size={'xl'}
      removeHeader={cityName ? false : true}
      overflow
      show
      onClose={handleCloseModal}
      className={'header-location-modal'}
    >
      <div className="full-page-overlay">
        <div className="location-map-wrapper set-location">
          <div className="google-map-box">
            <div className="autocomplete-search-box">
              <ReactGoogleAutocomplete
                style={{ width: '85%' }} // Adjusted width to fit the icon
                onPlaceSelected={(place) => {
                  setMapSearchCoordinates({
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                  });
                }}
                options={{
                  types: ['geocode'], // 'geocode' for comprehensive results
                  componentRestrictions: { country: 'in' },
                  fields: [
                    'address_components',
                    'geometry',
                    'formatted_address'
                  ] // Request specific fields
                }}
              />
              <button
                className="location-icon-button"
                onClick={requestLocationPermission}
              >
                <Icon
                  icon="mdi:crosshairs-gps"
                  width="24"
                  height="24"
                />
              </button>
            </div>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={center}
              zoom={13}
              onClick={(e) => {
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                handleMapClick(lat, lng);
              }}
              onLoad={(map) => (mapRef.current = map)} // Save reference to the map
              options={{
                disableDefaultUI: true,
                clickableIcons: false
              }}
            >
              {mapPosition.lat && mapPosition.lng && (
                <Marker
                  position={{
                    lat: mapPosition.lat,
                    lng: mapPosition.lng
                  }}
                />
              )}
            </GoogleMap>
          </div>

          <div className="location-map-content">
            <h3>Choose Location</h3>
            <div className="location-map-inner">
              <div className="choose-location-modal">
                <form onSubmit={formik.handleSubmit}>
                  <div className="form-group create-group">
                    <SelectBox
                      options={relationList}
                      placeholder="Choose State *"
                      onChange={handleStateChange}
                      value={selectedState}
                    />
                  </div>
                  <div className="form-group create-group">
                    <SelectBox
                      options={cityList}
                      placeholder="Choose City *"
                      onChange={handleCityChange}
                      value={selectedCity}
                    />
                  </div>
                  <div className="form-group submit-action mt-4">
                    <button
                      disabled={!selectedCity?.value}
                      type="submit"
                      className="btn btn-md"
                    >
                      <span>Set Location</span>
                    </button>
                    {cityId && (
                      <button
                        onClick={handleCloseModal}
                        type="button"
                        className="btn btn-md btn-stroke"
                      >
                        <span>Cancel</span>
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showLocationMismatchModal && (
        <LocationChangedDialog
          open={showLocationMismatchModal}
          handleClose={handleCloseLocationMismatchModal}
          handleDefaultLocation={handleDefaultLocation}
          handleSetLocation={handleSetLocation}
        />
      )}
      {showDefaultLocationModal && (
        <DefaultLocationDialog
          open={showDefaultLocationModal}
          handleDefaultLocation={handleDefaultLocation}
          handleClose={() => setShowDefaultLocationModal(false)}
        />
      )}
      {showPermissionModal && (
        <LocationPermission
          show={showPermissionModal}
          handleRetry={handleRetryPermission}
          handleDefaultLocation={handleDefaultLocation}
        />
      )}
    </CustomModal>
  );
};

export default LocationModal;
